import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { debounceTime, forkJoin, merge, Subscription } from 'rxjs';
import { NewsService } from 'src/app/services/news.service';
import { News, Rubric } from 'src/app/classes/news';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { FormControl } from '@angular/forms';
import moment from 'moment';


@Component({
	selector: 'app-news',
	templateUrl: './news.component.html',
	styleUrls: [ './news.component.scss' ]
})
export class NewsComponent implements OnInit, OnDestroy {


	bInProgress = true;
	bError = false;
	subscriptions = new Subscription();

	rubrics: Rubric[] = [];
	shownRubrics: Rubric[] = [];
	newsPerRubric: { [key:string]: News[] } = {};

	publicationCtrl = new FormControl<""|"published"|"unpublished">( "" );
	periodCtrl = new FormControl<string>( "" );
	lastYears: string[] = [];
	txtCtrl = new FormControl<string>( "" );
	startDateCtrl = new FormControl<moment.Moment|null>( null );
	endDateCtrl = new FormControl<moment.Moment|null>( null );
	private readonly _currentYear = new Date().getFullYear();
	minEndDate = new Date( this._currentYear - 100, 0, 1 );
	maxStartDate = new Date( this._currentYear + 100, 11, 31 );

	bUnfolded: { [key:string]: boolean } = {};
	bUnfoldedNews: { [key:string]: boolean } = {};

	bAdmin = this.authenticationService.isAdmin();

	bMoreResults = false;
	currentPage = 0;


	constructor(
		private newsService: NewsService,
		public domSanitizer: DomSanitizer,
		private authenticationService: AuthenticationService
	) {

		const dt = new Date();
		for( let i=0; i<3; i++ ) {
			const year = dt.getFullYear();
			this.lastYears.push( "" + year );
			dt.setFullYear( year - 1 );
		}

	}


	ngOnInit() {

		this.newsService.getRubrics().subscribe( rubrics => {
			
			this.rubrics = rubrics.sort( (a,b) => a.name.localeCompare( b.name ) );

			if( !this.bAdmin ) {
				const subscribedRubricIds = this.newsService.getStoredSubscribedRubrics();
				// show only rubrics for which the user is subscribed
				this.rubrics = this.rubrics.filter( r => subscribedRubricIds.includes(r.id) );
			}

			this.getAllNews();

		});


		this.subscriptions.add(
			this.startDateCtrl.valueChanges.subscribe( startDate => {

				this.minEndDate = startDate
					? moment( startDate ).add( 1, "days" ).toDate()
					: new Date( this._currentYear - 100, 0, 1 );
	
			} )
		);
		this.subscriptions.add(
			this.endDateCtrl.valueChanges.subscribe( endDate => {

				this.maxStartDate = endDate
					? moment( endDate ).add( -1, "days" ).toDate()
					: new Date( this._currentYear + 100, 11, 31 );

			} )
		);

		this.subscriptions.add(

			merge(
				this.txtCtrl.valueChanges,
				this.periodCtrl.valueChanges,
				this.startDateCtrl.valueChanges,
				this.endDateCtrl.valueChanges,
				this.publicationCtrl.valueChanges
			).pipe(
				debounceTime(300)
			).subscribe( arr => {
				this.currentPage = 0;
				this.bMoreResults = false;
				this.filterNews();
			} )

		);

	}

	ngOnDestroy() {
		
		this.subscriptions.unsubscribe();

	}

	private getAllNews() {

		this.bMoreResults = false;
		this.currentPage = 0;
		this.bInProgress = true;

		this.newsService.getNews().subscribe( news => {

			this.bError = !news;
			this.bInProgress = false;

			if( !news ) return;

			this.shownRubrics = this.rubrics;

			const subscribedRubricIds = this.newsService.getStoredSubscribedRubrics();
			const allNews = this.bAdmin ? news : news
				.filter( n => n.bReleased )
				.filter( n => {
					for( const rubricId of subscribedRubricIds ) {
						if( n.rubrics.includes( rubricId ) ) return true;
					}
					return false;
				});

			this.orderNews( allNews );

		});

	}

	private filterNews() {

		const text = this.txtCtrl.value || "";

		const period = this.periodCtrl.value || "";
		const days = {
			last30days: 30,
			last60days: 60,
			last180days: 180,
			last365days: 365
		}[ period ] || null;
		let year: number|null = null;
		for( const lastYear of this.lastYears ) {
			if( lastYear == period ) {
				year = +period;
				break;
			}
		}
		let startTms = null;
		let endTms = null;
		if( period == "custom" ) {
			const startDt = this.startDateCtrl.value;
			if( startDt ) startTms = startDt.valueOf();
			const endDt = this.endDateCtrl.value;
			if( endDt ) endTms = endDt.valueOf();
		}

		const released = { "published": true, "unpublished": false, "": null }[ this.publicationCtrl.value || "" ];

		if( !text && !days && !year && (startTms === null) && (endTms === null) && (released === null) ) {
			this.getAllNews();
			return;
		}

		this.bInProgress = true;
		this.newsService.searchNews( text, days, year, startTms, endTms, released, this.currentPage ).subscribe( result => {

			this.bError = !result;
			this.bInProgress = false;
			if( !result ) return;

			this.bMoreResults = result.bMoreResults;

			this.orderNews( result.news );

			this.shownRubrics = [];
			for( const news of result.news ) {

				for( const rubricId of news.rubrics ) {

					if( !this.shownRubrics.find( r => r.id == rubricId ) ) {

						const rubric = this.rubrics.find( r => r.id == rubricId );
						if( rubric ) {
							this.shownRubrics.push( rubric );
						}

					}

				}

			}

		});

	}

	private orderNews( news: News[] ) {

		this.newsPerRubric = {};
		for( const rubric of this.rubrics ) {
			this.newsPerRubric[ rubric.id ] = news.filter( n => n.rubrics.includes(rubric.id) ) || [];
		}

		if( this.bAdmin ) {
			this.newsPerRubric[ '' ] = news.filter( n => (!n.rubrics || !n.rubrics.length) );
		}

	}

	switchPage( n: number ) {

		if( this.bInProgress ) return;

		const nextPage = this.currentPage + n;
		if( nextPage < 0 ) return;
		if(  ( nextPage > this.currentPage ) && !this.bMoreResults  ) return;

		this.currentPage = nextPage;

		this.filterNews();
	
	}

}

